import useAppSelector from 'hooks/useAppSelector';
import { CartItemProduct, CartItemVariation, ImageExtension, PriceInformation, PriceValue } from 'microshop-api';
import { Fragment } from 'react';
import styled from 'styled-components';
import { QuantityChangedFunction } from './CartItems';
import CartRowAddon from './CartRowAddon';
import CartRowArticle from './CartRowArticle';
import CartRowHeader from './CartRowHeader';
import getImage, { ImageType } from 'utils/getImage';
import CartRowInput from './CartRowInput';

type CartProductProps = {
    onChangeQuantity: QuantityChangedFunction;
    onRemove: (sku: string) => void;
    onSetComment: (sku: string, comment: string) => void;
    product: CartItemProduct;
    forceSize?: any;
    hideName?: boolean;
};

const CartRowProduct: React.FC<CartProductProps> = ({
    onChangeQuantity,
    onRemove,
    onSetComment,
    forceSize,
    product,
    hideName,
}) => {
    return (
        <Product className="mb-4">
            <CartRowHeader
                name={product.name}
                options={{ size: forceSize, hide: { name: hideName, date: forceSize === 'sm' } }}
                productNumber={product.number}
            />
            {product.variations?.map((v, j) => (
                <Variation
                    onChangeQuantity={onChangeQuantity}
                    onRemove={onRemove}
                    onSetComment={onSetComment}
                    forceSize={forceSize}
                    key={`${v.number}${j}`}
                    product={product}
                    variation={v}
                />
            ))}
        </Product>
    );
};

type VariationProps = {
    onChangeQuantity: QuantityChangedFunction;
    onRemove: (sku: string) => void;
    onSetComment: (sku: string, comment: string) => void;
    product: CartItemProduct;
    variation: CartItemVariation;
    forceSize?: any;
};

const Variation: React.FC<VariationProps> = ({
    onChangeQuantity,
    onRemove,
    onSetComment,
    variation,
    product,
    forceSize,
}) => {
    const isB2B = useAppSelector(({ shop }) => shop?.shop?.isB2BShop) ?? false;

    return (
        <>
            {variation.skus?.map((sku, i) => {
                const collectionId = variation.collectionId;
                const split = variation.split;

                return (
                    <Fragment key={`${sku.sku}-${i}`}>
                        <CartRowArticle
                            forceSize={forceSize}
                            invalidVariation={false}
                            img={getImage(variation?.image, ImageType.Preview, ImageExtension.Jpg)}
                            imgAlt={product?.number ?? ''}
                            name={`${variation.color || ''}${variation.color && sku.size ? ' / ' : ''}${
                                sku.size || ''
                            }`}
                            info={[sku.parsedSku!]}
                            sku={sku}
                            product={product}
                            price={sku.price?.str ?? ''}
                            total={sku.skuTotal || ''}
                            onQtyChange={onChangeQuantity}
                            variationNumber={variation.number!}
                            collectionId={collectionId}
                            split={split}
                            onRemoveColor={() => onRemove(sku.sku!)}
                            onSetComment={(comment) => onSetComment(sku.sku!, comment)}
                            hideDateCell={!isB2B}
                            quantity={sku.quantity}
                            disableQuantityEdit={(sku.inputs?.length ?? 0) > 0}
                            usePoints={sku.usePoints ?? false}
                        />

                        {sku.addOns?.map((addon, i) => (
                            <CartRowAddon
                                forceSize={forceSize}
                                invalidVariation={false}
                                key={`${addon.id}-${i}`}
                                img={''}
                                imgAlt={''}
                                name={`${addon.productName}`}
                                sku={sku}
                                info={[addon.productNumber!]}
                                addon={addon}
                                price={addon.price}
                                total={addon.total || ''}
                                onQtyChange={onChangeQuantity}
                                variationNumber={variation.number!}
                                collectionId={variation.collectionId}
                                split={variation.split}
                                usePoints={sku.usePoints ?? false}
                            />
                        ))}

                        {sku.inputs?.map((input, i) => (
                            <CartRowInput
                                index={i + 1}
                                forceSize={forceSize}
                                key={`${input.id}-${i}`}
                                input={input}
                                sku={sku}
                                onQtyChange={onChangeQuantity}
                                collectionId={variation.collectionId}
                                split={variation.split}
                                splitId={input.id}
                            />
                        ))}
                    </Fragment>
                );
            })}
        </>
    );
};

export default CartRowProduct;

export type RemoveQuantityData = {
    article: string;
    name?: string;
    productNumber: string;
    variationNumber?: string;
    price?: number;
    category?: string;
    currency?: string;
    isVariation?: boolean;
};

const Product = styled.div``;
